import {
  convertFromClientContactDataToClientContactResponse,
  convertFromClientContactResponseToClientContactData,
  IClientContactData,
  IClientContactResponse,
} from 'types/contacts/IClientContact';
import {
  convertFromLenderContactDataToLenderContactResponse,
  convertFromLenderContactResponseToLenderContactData,
  ILenderContactData,
  ILenderContactResponse,
} from 'types/contacts/ILenderContact';
import {
  convertFromIThirdPartyContactDataToIThirdPartyContactRequest,
  convertFromIThirdPartyContactRequestToIThirdPartyContactData,
  IThirdPartyContactData,
  IThirdPartyContactRequest,
} from 'types/contacts/IThirdPartyContact';
import { UUID } from 'types/shared/UUID';
import {
  convertIAPISettlementAgentDataToIAPISettlementAgentResponse,
  convertIAPISettlementAgentResponseToIAPISettlementAgentData,
  IAPISettlementAgentData,
  IAPISettlementAgentResponse,
} from 'types/clientPortal/SettlementAgent/IAPISettlementAgent';
import {
  convertIExternalAPIPartyDataToIExternalAPIPartyResponse,
  convertIExternalAPIPartyDataToIParty,
  convertIExternalAPIPartyResponseToIExternalAPIPartyData,
  IExternalAPIPartyData,
  IExternalAPIPartyResponse,
} from 'types/clientPortal/ExternalAPIParty/IExternalAPIParty';
import {
  convertIOrderCreatorResponseToIOrderCreatorData,
  IOrderCreatorData,
  IOrderCreatorResponse,
} from 'types/clientPortal/IOrderCreator';
import {
  convertIClientDataToIClientResponse,
  convertIClientResponseToIClientData,
  IClientData,
  IClientResponse,
} from 'types/clientPortal/IClient';
import {
  convertIClientOrderAddressResponseToIPropertyAddressData,
  convertIPropertyAddressDataToIClientOrderAddressResponse,
  IClientOrderAddressResponse,
} from 'types/clientPortal/Address/IClientOrderAddress';
import { eSolarPanels } from '../../ui-toolkit/orders/interfaces/eSolarPanels';
import {
  convertIAPIFeeDataToIAPIFeeResponse,
  convertIAPIFeeResponseToIAPIFeeData,
  IAPIFeeData,
  IAPIFeeResponse,
} from 'types/clientPortal/Fees/IAPIFee';
import { undefArrConversion, undefConversion } from 'utilities/shared/undefinedConversion';
import { IPropertyAddressData } from '../address/IPropertyAddressData';
import { eOrderGroupCategory } from '../../ui-toolkit/enums/eOrderGroupCategory';
import { eTransactionCategory } from '../../ui-toolkit/enums/eTransactionType';
import { ePartyPurpose } from '../../ui-toolkit/enums/ePartyPurpose';
import { eExternalOrderStatus } from '../../ui-toolkit/enums/eExternalOrderStatus';
import { ePropertyType } from '../../ui-toolkit/orders/interfaces/ePropertyType';
import { eOccupancyType } from '../../ui-toolkit/orders/interfaces/eOccupancyType';
import { eLoanType } from '../../ui-toolkit/orders/interfaces/eLoanType';
import { IParty } from '../../ui-toolkit/orders/interfaces/IParty';
import { ICountyData } from '../../ui-toolkit/orders/searchOrder/types/ICounty';

export interface IClientOrderResponse {
  id: UUID;
  file_number: string | null;
  z_file_number: string | null;
  loan_amount: number;
  transaction_category: eTransactionCategory;
  order_status: eExternalOrderStatus;
  fees: IAPIFeeResponse[];
  settlement_agent?: IAPISettlementAgentResponse;
  borrowers?: IExternalAPIPartyResponse[];
  buyers?: IExternalAPIPartyResponse[];
  parties?: IExternalAPIPartyResponse[];
  sellers?: IExternalAPIPartyResponse[];
  creator: IOrderCreatorResponse;
  client_company: IClientResponse;
  external_party_transaction_id?: string;
  rate_lock_expiration?: string;
  loan_id?: string | null;
  property_address: IClientOrderAddressResponse;
  property_type?: ePropertyType;
  occupancy_type?: eOccupancyType;
  loan_type?: eLoanType;
  solar_panels?: eSolarPanels;
  closes_on?: string;
  funds_on?: string;
  estimated_closes_on?: string;
  is_in_hoa?: boolean;
  lender_company?: {
    id: UUID;
    name: string;
  };
  estimated_property_value?: number;
  order_contacts?: {
    clientContacts: IClientContactResponse[];
    lenderContacts: ILenderContactResponse[];
    thirdPartyContacts: IThirdPartyContactRequest[];
  };
  owner_name?: string;
  county: ICountyData;
  relocation_employer_name?: string;
  order_group_id?: string;
  order_group_category?: eOrderGroupCategory;
}

export interface IClientOrderData {
  id: UUID;
  fileNumber: string;
  loanAmount: number;
  transactionCategory: eTransactionCategory;
  orderStatus: eExternalOrderStatus;
  fees: IAPIFeeData[];
  settlementAgent?: IAPISettlementAgentData;
  borrowers?: IExternalAPIPartyData[];
  buyers?: IExternalAPIPartyData[];
  parties?: IExternalAPIPartyData[];
  sellers?: IExternalAPIPartyData[];
  creator: IOrderCreatorData;
  clientCompany: IClientData;
  externalPartyTransactionId?: string;
  rateLockExpiration?: string;
  loanId?: string | null;
  propertyAddress: IPropertyAddressData;
  propertyType?: ePropertyType;
  occupancyType?: eOccupancyType;
  loanType?: eLoanType;
  solarPanels?: eSolarPanels;
  closesOn?: string;
  fundsOn?: string;
  estimatedClosesOn?: string;
  isInHOA?: boolean;
  estimatedPropertyValueCents?: number;
  lenderCompany?: {
    id: UUID;
    name: string;
  };
  ownerName?: string;

  orderContacts?: {
    clientContacts: IClientContactData[];
    lenderContacts: ILenderContactData[];
    thirdPartyContacts: IThirdPartyContactData[];
  };
  county: ICountyData;
  relocationEmployerName?: string;
  orderGroupId?: string;
  orderGroupCategory?: eOrderGroupCategory;
}

export const convertIClientOrderResponseToIClientOrderData = ({
  id,
  fees,
  buyers,
  borrowers,
  parties,
  sellers,
  creator,
  file_number,
  z_file_number,
  loan_amount,
  transaction_category,
  order_status,
  settlement_agent,
  client_company,
  external_party_transaction_id,
  rate_lock_expiration,
  loan_id,
  property_address,
  property_type,
  occupancy_type,
  loan_type,
  solar_panels,
  closes_on,
  funds_on,
  estimated_closes_on,
  order_contacts,
  is_in_hoa,
  lender_company,
  estimated_property_value,
  owner_name,
  county,
  relocation_employer_name,
  order_group_id,
  order_group_category,
}: IClientOrderResponse): IClientOrderData => ({
  id,
  fees: fees.map(convertIAPIFeeResponseToIAPIFeeData),
  borrowers: undefArrConversion(convertIExternalAPIPartyResponseToIExternalAPIPartyData, borrowers),
  buyers: undefArrConversion(convertIExternalAPIPartyResponseToIExternalAPIPartyData, buyers),
  parties: undefArrConversion(convertIExternalAPIPartyResponseToIExternalAPIPartyData, parties),
  sellers: undefArrConversion(convertIExternalAPIPartyResponseToIExternalAPIPartyData, sellers),
  creator: convertIOrderCreatorResponseToIOrderCreatorData(creator),
  fileNumber: (file_number || z_file_number)!,
  loanAmount: loan_amount,
  transactionCategory: transaction_category,
  orderStatus: order_status,
  settlementAgent: undefConversion(convertIAPISettlementAgentResponseToIAPISettlementAgentData, settlement_agent),
  clientCompany: convertIClientResponseToIClientData(client_company),
  externalPartyTransactionId: external_party_transaction_id,
  rateLockExpiration: rate_lock_expiration,
  loanId: loan_id,
  propertyAddress: convertIClientOrderAddressResponseToIPropertyAddressData(property_address),
  propertyType: property_type,
  occupancyType: occupancy_type,
  loanType: loan_type,
  solarPanels: solar_panels,
  closesOn: closes_on,
  fundsOn: funds_on,
  estimatedClosesOn: estimated_closes_on,
  isInHOA: is_in_hoa,
  estimatedPropertyValueCents: estimated_property_value,
  lenderCompany: lender_company,
  orderContacts: order_contacts
    ? {
        clientContacts: order_contacts.clientContacts.map(convertFromClientContactResponseToClientContactData),
        lenderContacts: order_contacts.lenderContacts.map(convertFromLenderContactResponseToLenderContactData),
        thirdPartyContacts: order_contacts.thirdPartyContacts.map(
          convertFromIThirdPartyContactRequestToIThirdPartyContactData
        ),
      }
    : undefined,
  ownerName: owner_name,
  county: county,
  relocationEmployerName: relocation_employer_name,
  orderGroupId: order_group_id,
  orderGroupCategory: order_group_category,
});

export const convertIClientOrderDataToIClientOrderResponse = ({
  id,
  fileNumber,
  loanAmount,
  transactionCategory,
  orderStatus,
  fees,
  externalPartyTransactionId,
  rateLockExpiration,
  solarPanels,
  lenderCompany,
  parties,
  sellers,
  creator,
  loanId,
  orderContacts,
  orderGroupId,
  orderGroupCategory,
  clientCompany,
  county,
  relocationEmployerName,
  buyers,
  borrowers,
  loanType,
  propertyType,
  propertyAddress,
  estimatedPropertyValueCents,
  estimatedClosesOn,
  closesOn,
  occupancyType,
  ownerName,
  settlementAgent,
  isInHOA,
  fundsOn,
}: IClientOrderData): IClientOrderResponse => ({
  id,
  file_number: fileNumber,
  z_file_number: null,
  loan_amount: loanAmount,
  transaction_category: transactionCategory,
  order_status: orderStatus,
  fees: fees.map(convertIAPIFeeDataToIAPIFeeResponse),
  settlement_agent: undefConversion(convertIAPISettlementAgentDataToIAPISettlementAgentResponse, settlementAgent),
  borrowers: undefArrConversion(convertIExternalAPIPartyDataToIExternalAPIPartyResponse, borrowers),
  buyers: undefArrConversion(convertIExternalAPIPartyDataToIExternalAPIPartyResponse, buyers),
  parties: undefArrConversion(convertIExternalAPIPartyDataToIExternalAPIPartyResponse, parties),
  sellers: undefArrConversion(convertIExternalAPIPartyDataToIExternalAPIPartyResponse, sellers),
  creator: creator,
  client_company: convertIClientDataToIClientResponse(clientCompany),
  external_party_transaction_id: externalPartyTransactionId,
  rate_lock_expiration: rateLockExpiration,
  loan_id: loanId,
  property_address: convertIPropertyAddressDataToIClientOrderAddressResponse(propertyAddress),
  property_type: propertyType,
  occupancy_type: occupancyType,
  loan_type: loanType,
  solar_panels: solarPanels,
  closes_on: closesOn,
  funds_on: fundsOn,
  estimated_closes_on: estimatedClosesOn,
  is_in_hoa: isInHOA,
  lender_company: lenderCompany,
  estimated_property_value: estimatedPropertyValueCents,
  order_contacts: orderContacts
    ? {
        clientContacts: orderContacts.clientContacts.map(convertFromClientContactDataToClientContactResponse),
        lenderContacts: orderContacts.lenderContacts.map(convertFromLenderContactDataToLenderContactResponse),
        thirdPartyContacts: orderContacts.thirdPartyContacts.map(
          convertFromIThirdPartyContactDataToIThirdPartyContactRequest
        ),
      }
    : undefined,
  owner_name: ownerName,
  county: county,
  relocation_employer_name: relocationEmployerName,
  order_group_id: orderGroupId,
  order_group_category: orderGroupCategory,
});

// the previous LenderOrderData object had a field called `borrowerBuyer`
export const deriveBorrowerBuyer = ({ borrowers, buyers, transactionCategory }: IClientOrderData): IParty[] => {
  if (!!borrowers) {
    let purpose = transactionCategory === eTransactionCategory.Purchase ? ePartyPurpose.buyer : ePartyPurpose.borrower;
    return borrowers.map((borrower: IExternalAPIPartyData) => ({
      ...convertIExternalAPIPartyDataToIParty(borrower),
      purpose,
    }));
  } else if (!!buyers) {
    let purpose = transactionCategory === eTransactionCategory.Purchase ? ePartyPurpose.borrower : ePartyPurpose.buyer;
    return buyers.map((buyer: IExternalAPIPartyData) => ({ ...convertIExternalAPIPartyDataToIParty(buyer), purpose }));
  }
  return [];
};

// the previous LenderOrderData object had a field called `seller`
export const deriveSellers = ({ sellers }: IClientOrderData): IParty[] => {
  if (!!sellers) {
    return sellers.map((seller: IExternalAPIPartyData) => ({
      ...convertIExternalAPIPartyDataToIParty(seller),
      purpose: ePartyPurpose.seller,
    }));
  }
  return [];
};
